import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import Modal from '../../components/Modal';
import { CenteredLoader } from '../../components/LoadingSpinner';
import { api } from '../../shared/api';
import { useCrewMembers, useCreateCrewMember, useUpdateCrewMember, useDeleteCrewMember } from '../../entities/crewMember';
import { readListJsSort, attachListJsSortPersistence } from '../../shared/useSortPreference';
import List from 'list.js';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../shared/permissions';
import Select from '../../components/form/AppSelect';
import { Alert, InputGroup } from 'react-bootstrap';
import { IconSearch, IconPlus, IconBriefcase } from '@tabler/icons-react';
import AppButton from '../../components/ui/AppButton';
import PageShell from '../../components/layout/PageShell';
import PageSection from '../../components/layout/PageSection';
import StatusBadge from '../../components/ui/StatusBadge';
import EmptyState from '../../components/feedback/EmptyState';
import '../../shared/ReactSelectDarkMode.css';

// Custom theme for React Select to support dark mode
const selectTheme = theme => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: 'var(--tblr-primary)',
    primary75: 'var(--tblr-primary-rgb)',
    primary50: 'var(--tblr-primary-rgb)',
    primary25: 'var(--tblr-primary-rgb)',
    danger: 'var(--tblr-danger)',
    dangerLight: 'var(--tblr-danger-rgb)',
    neutral0: 'var(--tblr-bg-forms)',
    neutral5: 'var(--tblr-bg-surface)',
    neutral10: 'var(--tblr-bg-surface)',
    neutral20: 'var(--tblr-border-color)',
    neutral30: 'var(--tblr-border-color)',
    neutral40: 'var(--tblr-body-color-secondary)',
    neutral50: 'var(--tblr-body-color-secondary)',
    neutral60: 'var(--tblr-body-color-secondary)',
    neutral70: 'var(--tblr-body-color-secondary)',
    neutral80: 'var(--tblr-body-color)',
    neutral90: 'var(--tblr-body-color)'
  }
});

// Debounce function to limit API calls during typing
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

function Crew() {
  const { t } = useTranslation();
  const { canManage } = usePermissions();

  // ── Local UI state ────────────────────────────────────────────────────────
  const [formErrors, setFormErrors] = useState(null);
  const [crewMemberToEdit, setCrewMemberToEdit] = useState(null);
  const [crewMemberToDelete, setCrewMemberToDelete] = useState(null);

  const [newCrewMemberShow, setNewCrewMemberShow] = useState(false);
  const [editCrewMemberShow, setEditCrewMemberShow] = useState(false);
  const [deleteCrewMemberShow, setDeleteCrewMemberShow] = useState(false);


  // React Select options for users (fetched separately due to pagination)
  const [userOptions, setUserOptions] = useState([]);

  const [newCrewMember, setNewCrewMember] = useState({
    first_name: '',
    last_name: '',
    email: '',
    telephone: '',
    mobile: '',
    profession_id: null,
    company_id: null,
    user_id: null,
    default_hourly_rate: '',
    default_daily_rate: ''
  });

  const handleCloseNewCrewMember = () => setNewCrewMemberShow(false);
  const handleShowNewCrewMember = () => {
    setFormErrors(null);
    setNewCrewMemberShow(true);
  };

  const handleCloseEditCrewMember = () => setEditCrewMemberShow(false);
  const handleShowEditCrewMember = () => setEditCrewMemberShow(true);

  const handleCloseDeleteCrewMember = () => setDeleteCrewMemberShow(false);
  const handleShowDeleteCrewMember = () => setDeleteCrewMemberShow(true);

  const listInstance = useRef(null);
  const tableRef = useRef(null);

  // ── Server state (React Query) ────────────────────────────────────────────
  const {
    data: crewMembers = [],
    isLoading,
    error,
  } = useCrewMembers();

  const { data: professions = [] } = useQuery({
    queryKey: ['professions'],
    queryFn: () => api.get('crew/api/professions/'),
  });

  const { data: companies = [] } = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.get('companies/api/companies/'),
  });

  // Derived options (professions & companies — client-side filtering via react-select)
  const professionOptions = useMemo(
    () => professions.map(p => ({ value: p.id, label: p.name })),
    [professions],
  );

  const companyOptions = useMemo(
    () => companies.map(c => ({ value: c.id, label: c.company_name })),
    [companies],
  );

  // Users: fetched once on mount; debounced search updates local options
  const fetchUsers = useCallback(async (searchTerm = '') => {
    try {
      const url = searchTerm
        ? `accounts/api/users/?search=${encodeURIComponent(searchTerm)}`
        : 'accounts/api/users/';
      const response = await api.get(url);
      const usersData = response?.results ?? (Array.isArray(response) ? response : []);
      const options = usersData.map(user => ({
        value: user.id,
        label: user.email
          ? `${user.username} (${user.email})`
          : user.first_name || user.last_name
          ? `${user.username} (${user.first_name} ${user.last_name})`.trim()
          : user.username,
      }));
      setUserOptions(options);
    } catch (err) {
      console.warn('Users endpoint not accessible:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // ── Mutations ─────────────────────────────────────────────────────────────
  const createCrewMember = useCreateCrewMember();
  const createMutation = {
    isPending: createCrewMember.isPending,
    mutate: (payload) => createCrewMember.mutate(payload, {
      onSuccess: () => {
        setNewCrewMember({
          first_name: '', last_name: '', email: '',
          telephone: '', mobile: '',
          profession_id: null, company_id: null, user_id: null,
          default_hourly_rate: '', default_daily_rate: '',
        });
        handleCloseNewCrewMember();
      },
      onError: (err) => setFormErrors({ error: err.message || t('crew.errors.failedToCreate') }),
    }),
  };

  const updateCrewMember = useUpdateCrewMember();
  const updateMutation = {
    isPending: updateCrewMember.isPending,
    mutate: ({ id, ...payload }) => updateCrewMember.mutate({ id, data: payload }, {
      onSuccess: () => {
        handleCloseEditCrewMember();
        setCrewMemberToEdit(null);
      },
      onError: (err) => setFormErrors({ error: err.message || t('crew.errors.failedToUpdate') }),
    }),
  };

  const deleteCrewMember = useDeleteCrewMember();
  const deleteMutation = {
    isPending: deleteCrewMember.isPending,
    mutate: (id) => deleteCrewMember.mutate(id, {
      onSuccess: () => {
        handleCloseDeleteCrewMember();
        setCrewMemberToDelete(null);
      },
      onError: (err) => setFormErrors({ error: err.message || t('crew.errors.failedToDelete') }),
    }),
  };

  // ── Get available user options (exclude already linked) ───────────────────
  const getAvailableUserOptions = (currentUserId = null) => {
    const linkedUserIds = crewMembers
      .filter(m => m.user?.id)
      .map(m => m.user.id);
    return userOptions.filter(
      opt => (currentUserId && opt.value === currentUserId) || !linkedUserIds.includes(opt.value),
    );
  };

  // ── Debounced dropdown searches ───────────────────────────────────────────
  const debouncedUserSearch = useCallback(
    debounce((inputValue) => {
      fetchUsers(inputValue);
    }, 300),
    [fetchUsers],
  );

  useEffect(() => {
    // Initialize list.js for searching, sorting and pagination
    if (!isLoading && crewMembers.length > 0 && tableRef.current) {
      listInstance.current = new List('table-default', {
        sortClass: 'table-sort',
        listClass: 'table-tbody',
        valueNames: ['first_name', 'last_name', 'email', 'profession_name', 'company_name'],
        page: 10,
        pagination: [{
          paginationClass: "pagination",
          innerWindow: 3,
          outerWindow: 2
        }]
      });

      // Restore saved sort state
      const saved = readListJsSort('crew');
      if (saved?.col) {
        listInstance.current.sort(saved.col, { order: saved.order || 'asc' });
      }

      // Persist sort state on each click
      const cleanupSort = attachListJsSortPersistence('crew', tableRef);

      return () => {
        cleanupSort();
        if (listInstance.current) {
          try {
            if (typeof listInstance.current.destroy === 'function') {
              listInstance.current.destroy();
            } else {
              listInstance.current = null;
            }
          } catch (error) {
            console.warn('Error cleaning up list.js instance:', error);
            listInstance.current = null;
          }
        }
      };
    }
  }, [isLoading, crewMembers]);

  const handleSearchInput = (e) => {
    if (listInstance.current) {
      listInstance.current.search(e.target.value);
    }
  };

  const handleNewCrewMemberChange = (e) => {
    const { name, value } = e.target;
    setNewCrewMember(prev => ({ ...prev, [name]: value }));
  };

  const handleNewCrewMemberSelectChange = (name, selectedOption) => {
    setNewCrewMember(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : null
    }));
  };

  const handleEditCrewMember = (crewMember) => {
    setFormErrors(null);
    setCrewMemberToEdit({
      ...crewMember,
      first_name: crewMember.first_name || crewMember.display_first_name || '',
      last_name: crewMember.last_name || crewMember.display_last_name || '',
      email: crewMember.email || crewMember.display_email || '',
      telephone: crewMember.company?.telephone || '',
      mobile: crewMember.company?.mobile || '',
      profession_id: crewMember.profession?.id || null,
      company_id: crewMember.company?.id || null,
      user_id: crewMember.user?.id || null,
      default_hourly_rate: crewMember.default_hourly_rate ?? '',
      default_daily_rate: crewMember.default_daily_rate ?? ''
    });
    handleShowEditCrewMember()
  };

  const handleShowDeleteCrewMemberModal = (crewMember) => {
    setCrewMemberToDelete(crewMember);
    handleShowDeleteCrewMember();
  };

  const handleEditCrewMemberChange = (e) => {
    const { name, value } = e.target;
    setCrewMemberToEdit(prev => ({ ...prev, [name]: value }));
  };

  const handleEditCrewMemberSelectChange = (name, selectedOption) => {
    setCrewMemberToEdit(prev => ({
      ...prev,
      [name]: selectedOption ? selectedOption.value : null
    }));
  };

  const handleSubmitNewCrewMember = (e) => {
    e.preventDefault();
    setFormErrors(null);

    if (!newCrewMember.first_name || !newCrewMember.last_name) {
      setFormErrors({ error: t('crew.errors.firstNameRequired') + ' ' + t('crew.errors.lastNameRequired') });
      return;
    }
    if (!newCrewMember.profession_id) {
      setFormErrors({ error: t('crew.errors.professionRequired') });
      return;
    }

    createMutation.mutate({
      first_name: newCrewMember.first_name,
      last_name: newCrewMember.last_name,
      email: newCrewMember.email || '',
      telephone: newCrewMember.telephone || '',
      mobile: newCrewMember.mobile || '',
      profession_id: newCrewMember.profession_id,
      company_id: newCrewMember.company_id,
      user_id: newCrewMember.user_id,
      default_hourly_rate: newCrewMember.default_hourly_rate || 0,
      default_daily_rate: newCrewMember.default_daily_rate || 0,
    });
  };

  const handleSubmitEditCrewMember = (e) => {
    e.preventDefault();
    setFormErrors(null);

    if (!crewMemberToEdit.first_name || !crewMemberToEdit.last_name) {
      setFormErrors({ error: t('crew.errors.firstNameRequired') + ' ' + t('crew.errors.lastNameRequired') });
      return;
    }
    if (!crewMemberToEdit.profession_id) {
      setFormErrors({ error: t('crew.errors.professionRequired') });
      return;
    }

    updateMutation.mutate({
      id: crewMemberToEdit.id,
      first_name: crewMemberToEdit.first_name,
      last_name: crewMemberToEdit.last_name,
      email: crewMemberToEdit.email || '',
      telephone: crewMemberToEdit.telephone || '',
      mobile: crewMemberToEdit.mobile || '',
      profession_id: crewMemberToEdit.profession_id,
      company_id: crewMemberToEdit.company_id,
      user_id: crewMemberToEdit.user_id,
      default_hourly_rate: crewMemberToEdit.default_hourly_rate || 0,
      default_daily_rate: crewMemberToEdit.default_daily_rate || 0,
    });
  };

  const handleDeleteCrewMember = (id) => {
    deleteMutation.mutate(id);
  };

  const renderCrewMemberForm = (crewMember = null, isEdit = false) => {
    const data = crewMember || newCrewMember;
    const handleChange = isEdit ? handleEditCrewMemberChange : handleNewCrewMemberChange;
    const handleSelectChange = isEdit ? handleEditCrewMemberSelectChange : handleNewCrewMemberSelectChange;

    return (
      <>
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t('crew.form.firstName')}</label>
              <input
                type="text"
                name="first_name"
                className="form-control"
                value={data.first_name || ''}
                onChange={handleChange}
                required
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t('crew.form.lastName')}</label>
              <input
                type="text"
                name="last_name"
                className="form-control"
                value={data.last_name || ''}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">{t('crew.form.email')}</label>
          <input
            type="email"
            name="email"
            className="form-control"
            value={data.email || ''}
            onChange={handleChange}
          />
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t('crew.form.mobile', 'Mobil')}</label>
              <input
                type="tel"
                name="mobile"
                className="form-control"
                value={data.mobile || ''}
                onChange={handleChange}
                placeholder={t('crew.form.mobilePlaceholder', '+420 ...')}
              />
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t('crew.form.telephone', 'Telefon')}</label>
              <input
                type="tel"
                name="telephone"
                className="form-control"
                value={data.telephone || ''}
                onChange={handleChange}
                placeholder={t('crew.form.telephonePlaceholder', '+420 ...')}
              />
            </div>
          </div>
        </div>

        <div className="mb-3">
          <label className="form-label">{t('crew.form.profession')}</label>
          <Select
            name="profession_id"
            classNamePrefix="react-select"
            className="react-select-container"
            value={professionOptions.find(option => option.value === data.profession_id) || null}
            onChange={selectedOption => handleSelectChange('profession_id', selectedOption)}
            options={professionOptions}
            placeholder={t('crew.form.selectProfession')}
            isSearchable
            isClearable
            theme={selectTheme}
            noOptionsMessage={() => t('common.noOptions')}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">{t('crew.form.company')}</label>
          <Select
            name="company_id"
            classNamePrefix="react-select"
            className="react-select-container"
            value={companyOptions.find(option => option.value === data.company_id) || null}
            onChange={selectedOption => handleSelectChange('company_id', selectedOption)}
            options={companyOptions}
            placeholder={t('crew.form.selectCompany')}
            isSearchable
            isClearable
            theme={selectTheme}
            noOptionsMessage={() => t('common.noOptions')}
          />
        </div>

        <div className="mb-3">
          <label className="form-label">{t('crew.form.linkUserAccount')}</label>
          <Select
            name="user_id"
            classNamePrefix="react-select"
            className="react-select-container"
            value={userOptions.find(option => option.value === data.user_id) || null}
            onChange={selectedOption => handleSelectChange('user_id', selectedOption)}
            onInputChange={(inputValue) => {
              if (inputValue) {
                debouncedUserSearch(inputValue);
              } else {
                fetchUsers();
              }
            }}
            options={getAvailableUserOptions(data.user_id)}
            placeholder={t('crew.form.selectUser')}
            isSearchable
            isClearable
            theme={selectTheme}
            noOptionsMessage={() => t('common.noOptions')}
            loadingMessage={() => t('common.loading')}
          />
          <small className="form-text text-muted">
            {t('crew.form.userAccountHelp')}
          </small>
        </div>

        <hr />
        <h4>{t('crew.form.defaultRates')}</h4>
        <div className="row">
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t('crew.form.defaultHourlyRate')}</label>
              <div className="input-group">
                <input
                  type="number"
                  name="default_hourly_rate"
                  className="form-control"
                  value={data.default_hourly_rate ?? ''}
                  onChange={handleChange}
                  min="0"
                  step="any"
                />
                <span className="input-group-text">{t('crew.form.perHour')}</span>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="mb-3">
              <label className="form-label">{t('crew.form.defaultDailyRate')}</label>
              <div className="input-group">
                <input
                  type="number"
                  name="default_daily_rate"
                  className="form-control"
                  value={data.default_daily_rate ?? ''}
                  onChange={handleChange}
                  min="0"
                  step="any"
                />
                <span className="input-group-text">{t('crew.form.perDay')}</span>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <PageShell
        pretitle={t('crew.overview')}
        title={t('crew.title')}
        description={t('crew.subtitle', 'Seznam členů týmu, jejich profesí a napojení na uživatelské účty v jednom přehledu.')}
        actions={(
          <>
            <AppButton as={Link} to="/crew/professions" variant="outline-secondary" icon={<IconBriefcase size={16} />}>
              {t('crew.professions.manageProfessions')}
            </AppButton>
            {canManage && (
              <AppButton variant="primary" icon={<IconPlus size={16} />} onClick={handleShowNewCrewMember}>
                {t('crew.newCrewMember')}
              </AppButton>
            )}
          </>
        )}
      >
        {formErrors && (
          <Alert variant="danger" className="app-inline-alert" dismissible>
            {typeof formErrors === 'string'
              ? formErrors
              : Object.entries(formErrors).map(([key, value]) => (
                  <div key={key}><strong>{key}:</strong> {value}</div>
                ))}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="app-inline-alert" dismissible>
            {error}
          </Alert>
        )}

        <PageSection
          title={t('crew.title')}
          description={t('crew.sectionDescription', 'Vyhledej člena týmu, zkontroluj profesi a otevři rychlé akce bez opuštění seznamu.')}
          actions={
            <InputGroup className="app-search">
              <InputGroup.Text>
                <IconSearch size={16} />
              </InputGroup.Text>
              <input
                type="text"
                id="table-search"
                className="form-control"
                placeholder={t('crew.searchPlaceholder')}
                onChange={handleSearchInput}
              />
            </InputGroup>
          }
          bodyClassName="p-0"
        >
          {isLoading ? (
            <CenteredLoader message={t('crew.loading')} />
          ) : crewMembers.length === 0 ? (
            <EmptyState
              title={t('crew.noCrewMembersFound')}
              message={t('crew.emptyMessage', 'Zatím tu nejsou žádní členové týmu nebo aktivní filtr nic nevrátil.')}
              action={canManage ? (
                <AppButton variant="primary" icon={<IconPlus size={16} />} onClick={handleShowNewCrewMember}>
                  {t('crew.newCrewMember')}
                </AppButton>
              ) : null}
            />
          ) : (
            <div id="table-default" className="table-responsive" ref={tableRef}>
              <table className="table table-hover table-vcenter app-table mb-0">
                <thead>
                  <tr>
                    <th><button className="table-sort" data-sort="first_name">{t('crew.table.firstName')}</button></th>
                    <th><button className="table-sort" data-sort="last_name">{t('crew.table.lastName')}</button></th>
                    <th><button className="table-sort" data-sort="email">{t('crew.table.email')}</button></th>
                    <th><button className="table-sort" data-sort="profession_name">{t('crew.table.profession')}</button></th>
                    <th><button className="table-sort" data-sort="company_name">{t('crew.table.company')}</button></th>
                    <th>{t('crew.table.userAccount')}</th>
                    <th>{t('crew.table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="table-tbody">
                  {crewMembers.map(crewMember => (
                    <tr key={crewMember.id}>
                      <td className="first_name">
                        <a href={`/crew/${crewMember.id}`}>{crewMember.display_first_name || crewMember.first_name}</a>
                      </td>
                      <td className="last_name">{crewMember.display_last_name || crewMember.last_name}</td>
                      <td className="email">{crewMember.display_email || crewMember.email}</td>
                      <td className="profession_name">{crewMember.profession?.name || '-'}</td>
                      <td className="company_name">{crewMember.company?.company_name || '-'}</td>
                      <td>
                        {crewMember.user ? (
                          <StatusBadge color="#2fb344" label={crewMember.user.username} />
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        {canManage && (
                          <div className="btn-group">
                            <AppButton
                              variant="outline-secondary"
                              size="sm"
                              icon={<i className="ti ti-edit" aria-hidden="true" />}
                              onClick={() => handleEditCrewMember(crewMember)}
                              title={t('common.edit')}
                            />
                            <AppButton
                              variant="outline-danger"
                              size="sm"
                              icon={<i className="ti ti-trash" aria-hidden="true" />}
                              onClick={() => handleShowDeleteCrewMemberModal(crewMember)}
                              title={t('common.delete')}
                            />
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <ul className="pagination"></ul>
            </div>
          )}
        </PageSection>
      </PageShell>

      {/* New Crew Member Modal */}
      <Modal
        id="modal-crew-member"
        title={t('crew.newCrewMember')}
        show={newCrewMemberShow}
        onHide={handleCloseNewCrewMember}
        size="lg"
        fullscreen="sm-down"
        footer={
          <>
            <AppButton variant="ghost" onClick={handleCloseNewCrewMember}>{t('common.cancel')}</AppButton>
            <AppButton
              variant="primary"
              icon={<IconPlus size={16} />}
              className="ms-auto"
              onClick={handleSubmitNewCrewMember}
              loading={createMutation.isPending}
              loadingText={t('common.saving')}
            >
              {t('crew.form.create')}
            </AppButton>
          </>
        }
      >
        {renderCrewMemberForm()}
      </Modal>

      {/* Edit Crew Member Modal */}
      {crewMemberToEdit && (
        <Modal
          id={`modal-edit-crew-member-${crewMemberToEdit.id}`}
          title={t('crew.form.edit')}
          show={editCrewMemberShow}
          onHide={handleCloseEditCrewMember}
          size="lg"
          fullscreen="sm-down"
          footer={
            <>
              <AppButton
                variant="ghost"
                onClick={() => {
                  handleCloseEditCrewMember();
                  setCrewMemberToEdit(null);
                }}
              >
                {t('common.cancel')}
              </AppButton>
              <AppButton
                variant="primary"
                onClick={handleSubmitEditCrewMember}
                loading={updateMutation.isPending}
                loadingText={t('common.saving')}
              >
                {t('crew.form.saveChanges')}
              </AppButton>
            </>
          }
        >
          {renderCrewMemberForm(crewMemberToEdit, true)}
        </Modal>
      )}

      {/* Delete Crew Member Modal */}
      {crewMemberToDelete && (
        <Modal
          id={`modal-delete-crew-member-${crewMemberToDelete.id}`}
          title={t('common.confirm')}
          show={deleteCrewMemberShow}
          onHide={handleCloseDeleteCrewMember}
          footer={
            <>
              <AppButton
                variant="ghost"
                onClick={() => {
                  handleCloseDeleteCrewMember();
                  setCrewMemberToDelete(null);
                }}
              >
                {t('common.cancel')}
              </AppButton>
              <AppButton
                variant="danger"
                onClick={() => handleDeleteCrewMember(crewMemberToDelete.id)}
                loading={deleteMutation.isPending}
                loadingText={t('common.deleting')}
              >
                {t('common.delete')}
              </AppButton>
            </>
          }
        >
          <p>
            {t('crew.form.confirmDelete', { name: `${crewMemberToDelete.display_first_name || crewMemberToDelete.first_name} ${crewMemberToDelete.display_last_name || crewMemberToDelete.last_name}` })}
          </p>
        </Modal>
      )}
      </>
  );
}

export default Crew;
